import { useMutation } from "@apollo/client";
import { BULK_EDIT_ASSETS } from "../../graphql/mutations/assets/assets.mutations";

// Sent in chunks (rather than one giant request) so the caller can report
// real progress as each chunk resolves, instead of one opaque wait for
// however long the entire batch takes.
const CHUNK_SIZE = 50;

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const useBulkEditAssets = () => {
  const [bulkEditAssetsMutation, { loading, error }] =
    useMutation(BULK_EDIT_ASSETS);

  // onProgress(processedCount, totalCount) is called after each chunk resolves.
  // `resumeFrom` is a previous partial result ({ updated, failed }) — pass it
  // back in on retry to continue from where a mid-way failure left off,
  // instead of resubmitting rows that already succeeded.
  const bulkEditAssets = async (assets, onProgress, resumeFrom) => {
    const updated = [...(resumeFrom?.updated ?? [])];
    const failed = [...(resumeFrom?.failed ?? [])];
    const remaining = assets.slice(updated.length + failed.length);

    try {
      for (const batch of chunkArray(remaining, CHUNK_SIZE)) {
        const res = await bulkEditAssetsMutation({ variables: { assets: batch } });
        const result = res?.data?.bulkEditAssets ?? { updated: [], failed: [] };
        updated.push(...(result.updated ?? []));
        failed.push(...(result.failed ?? []));
        onProgress?.(updated.length + failed.length, assets.length);
      }
      return { updated, failed };
    } catch (err) {
      console.error(`Error updating assets: ${err?.message}`);
      const resumableError = new Error(err?.message || "Update failed");
      resumableError.partialResult = { updated, failed };
      throw resumableError;
    }
  };

  return { bulkEditAssets, loading, error };
};

export default useBulkEditAssets;
